import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {OrderItem, OrderQueryParams, OrderStatistics, OrderStatus, UpdateRecipientInfo} from '@/types/order';
import {orderService} from '@/services/orderService';

interface OrderState {
  items: OrderItem[];
  total: number;
  statistics: OrderStatistics;
  loading: boolean;
  error: string | null;
}

const initialState: OrderState = {
  items: [],
  total: 0,
  statistics: {
    totalOrders: 0,
    totalAmount: 0,
    pendingOrders: 0,
    completedOrders: 0,
    inUseOrders: 0,
    totalDeposit: 0,
  },
  loading: false,
  error: null,
};

interface CompensationFormItem {
  compensateReason: string;
  compensateAmount: number;
}

export const fetchOrders = createAsyncThunk(
  'order/fetchOrders',
  async (params: OrderQueryParams) => {
    const response = await orderService.getOrderList(params);
    return response;
  },
);

export const fetchStatistics = createAsyncThunk(
  'order/fetchStatistics',
  async () => {
    const response = await orderService.getOrderStatistics();
    return response;
  },
);

export const cancelOrder = createAsyncThunk(
  'order/cancelOrder',
  async ({
    orderNo,
    celCode,
    celReason,
    addToBlacklist,
  }: {
    orderNo: string;
    celCode: string;
    celReason: string;
    addToBlacklist?: boolean;
  }) => {
    const response = await orderService.cancelOrder(orderNo, celCode, celReason, addToBlacklist);
    return response;
  },
);


export const updateOrderRemark = createAsyncThunk(
  'order/updateOrderRemark',
  async ({ orderNo, remark }: { orderNo: string; remark: string }) => {
    const response = await orderService.updateOrderRemark(orderNo, remark);
    return response;
  },
);

export const updateOrderPrice = createAsyncThunk(
  'order/updateOrderPrice',
  async ({ orderNo, promotionDiscountAmount }: { orderNo: string; promotionDiscountAmount: number }) => {
    const response = await orderService.updateOrderPrice(orderNo, promotionDiscountAmount);
    return response;
  },
);

export const skipAssessment = createAsyncThunk(
  'order/skipAssessment',
  async ({ orderNo }: { orderNo: string }) => {
    const response = await orderService.skipAssessment(orderNo);
    return response;
  },
);

export const approveOrder = createAsyncThunk(
  'order/approveOrder',
  async ({ orderNo, examineReason }: { orderNo: string; examineReason: string }) => {
    const response = await orderService.approveOrder(orderNo, examineReason);
    return response;
  },
);

export const repDeposit = createAsyncThunk(
  'order/repDeposit',
  async ({ orderNo, replenishDeposit, settlementId }: { orderNo: string; replenishDeposit: number ;settlementId: number | null}) => {
    const response = await orderService.repDeposit(orderNo, replenishDeposit, settlementId);
    return response;
  },
);

export const updateImeiOrSn = createAsyncThunk(
  'order/updateImeiOrSn',
  async ({ orderNo, serialNumber }: { orderNo: string; serialNumber: string }) => {
    const response = await orderService.updateImeiOrSn(orderNo, serialNumber);
    return response;
  },
);


export const updateOrderStatus = createAsyncThunk(
  'order/updateStatus',
  async ({ id, status, remarks }: { id: number; status: OrderStatus; remarks?: string }) => {
    const response = await orderService.updateOrderStatus(id, status, remarks);
    return response;
  },
);

// 在 orderSlice.ts 中添加
export const settlementOrder = createAsyncThunk(
  'order/settlementOrder',
  async ({ orderNo }: { orderNo: string }) => {
    const response = await orderService.settlementOrder(orderNo);
    return response;
  },
);


// 在 orderSlice.ts 中添加
export const completeOrder = createAsyncThunk(
  'order/completeOrder',
  async ({ orderNo, fulfillmentFinishStatus }: { orderNo: string; fulfillmentFinishStatus: string }) => {
    const response = await orderService.completeOrder(orderNo, fulfillmentFinishStatus);
    return response;
  },
);


export const renewOrder = createAsyncThunk(
  'order/renewOrder',
  async ({ orderNo, leaseRenewStart, leaseRenewEnd, activeRenewalFlag, actualRentAmount }: {
    orderNo: string;
    leaseRenewStart: string;
    leaseRenewEnd: string;
    activeRenewalFlag: boolean;
    actualRentAmount: number;
  }) => {
    const response = await orderService.renewOrder(orderNo, leaseRenewStart, leaseRenewEnd, activeRenewalFlag, actualRentAmount);
    return response;
  },
);

export const overdueRenewOrder = createAsyncThunk(
  'order/overdueRenewOrder',
  async ({ orderId, overdueAmount }: { orderId: number; overdueAmount?: number }) => {
    const response = await orderService.overdueRenewOrder(orderId, overdueAmount);
    return response;
  },
);

export const fetchRiskInfo = createAsyncThunk(
  'order/fetchRiskInfo',
  async ({ userId }: { userId: number }) => {
    const response = await orderService.fetchRiskInfo(userId);
    return response;
  },
);
export const cancelCancelOrder = createAsyncThunk(
  'order/cancelCancelOrder',
  async ({ orderNo}: { orderNo: string;}) => {
    const response = await orderService.cancelCancelOrder(orderNo);
    return response;
  },
);
export const updateRecipientInfo = createAsyncThunk(
  'order/updateRecipientInfo',
  async (params: UpdateRecipientInfo) => {
    const response = await orderService.updateRecipientInfo(params);
    return response;
  },
);
const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取订单列表失败';
      })
      .addCase(fetchStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch statistics';
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.map(item =>
          item.id === action.payload.id ? action.payload : item,
        );
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update order status';
      })
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;

      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update order status';
      })
      .addCase(updateOrderRemark.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderRemark.fulfilled, (state, action) => {
        state.loading = false;

      })
      .addCase(updateOrderRemark.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update order status';
      })
      .addCase(updateOrderPrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderPrice.fulfilled, (state, action) => {
        state.loading = false;

      })
      .addCase(updateOrderPrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update order price';
      })
      .addCase(approveOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveOrder.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(approveOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to approve order';
      })
      .addCase(repDeposit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(repDeposit.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(repDeposit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to repDeposit order';
      })
      .addCase(skipAssessment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(skipAssessment.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(skipAssessment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to skipAssessmentorder';
      });
  },
});

export default orderSlice.reducer;
